import { IconButton } from "@/subframe/components/IconButton";
import { TextField } from "@/subframe/components/TextField";
import { Button } from "@/subframe/components/Button";
import { DropdownMenu } from "@/subframe/components/DropdownMenu";
import * as SubframeCore from "@subframe/core";
import { ChatList } from "@/subframe/components/ChatList";
import { ActionCard } from "@/subframe/components/ActionCard";
import Friend from "./Friend";
import ChatRoom from "./ChatRoom";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addSelectedLabel,
  getConversation,
  getRecipients,
  newMessageByWebhook,
  newRecipientWithMessage,
  removeSelectedLabel,
  resetRecipients,
  setCurrentRecipient,
  updateMessageStatus,
  updateRecipient,
} from "../redux/reducers/messengerSlice";
import { AppDispatch, RootState } from "../redux/store";
import { Item } from "../redux/interfaces/reducersInterfaces";
import {
  fetchLabels,
} from "../redux/reducers/authSlice";
import Settings from "./Settings";
import LabelCreation from "./subcomponents/LabelCreation";
import DeleteLabelPopup from "./subcomponents/DeleteLabelPopup";
import SwitchToAiAssistantPopup from "./subcomponents/SwitchToAiAssistantPopup";
import { io, Socket } from "socket.io-client";
import { Message } from "../Interfaces/messagesInterface";
import SwitchToHumanAgent from "./subcomponents/SwitchToHunamAgentPopup";

//swiper
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import { Loader } from "@/subframe/components/Loader";

import ChatSendNewMessage from "./ChatSendNewMessage";
import {
  selectDetailsLoading,
  selectPhoneNumbers,
} from "../redux/reducers/dealPersonDetailsSlice";
import {
  getPrimaryPhoneSearchQuery,
  isPipedriveDetailsContext,
  recipientMatchesSearchQuery,
} from "../Utils/pipedriveContext";

function Messenger() {
  const { currentRecipient, selectedLabels } = useSelector(
    (state: RootState) => state.messenger
  );
  const { labels } = useSelector((state: RootState) => state.auth);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const dispatch = useDispatch<AppDispatch>();
  const scrollRef = useRef<HTMLDivElement>(null);
  const socket = useRef<Socket | null>(null);
  const [isSettingOpen, setIsSettingOpen] = useState(false);
  const [isChatSendNewMessageOpen, setIsChatSendNewMessageOpen] = useState(false);
  const [isLabelCreationOpen, setIsLabelCreationOpen] = useState(false);
  const [labelId, setLabelId] = useState<number>();
  const [isLabelDeleteOpen, setIsLabelDeleteOpen] = useState(false);
  const [isSwitchOn, setIsSwitchOn] = useState<boolean>(
    currentRecipient?.is_ai_assistant ? true : false
  );
  const [isSwitchToAiAssistantPopupOpen, setIsSwitchToAiAssistantPopupOpen] =
    useState(false);
  const [isSwitchToHumanAgentPopupOpen, setIsSwitchToHumanAgentPopupOpen] =
    useState(false);
  // const [isSelectDateAndTimePopupOpen, setSelectDateAndTimePopupOpen] =
  //   useState(false);
  const [filter, setFilter] = useState<"all" | "unread">("all");

  // Language/video section state
  const [selectedLanguage, setSelectedLanguage] = useState<'en' | 'es' | 'pt' | 'fr'>('en');
  const videoUrls: Record<string, string> = {
    en: 'https://www.youtube.com/embed/zB3DtzEqrKQ',
    es: 'https://www.youtube.com/embed/euVoq7BK-LI',
    pt: 'https://www.youtube.com/embed/MO_G7iRVNJY',
    fr: 'https://www.youtube.com/embed/lVvQRENWkM8',
  };

  const { system_user_id } = useSelector(
    (state: RootState) => state.auth
  );
  const phoneNumbers = useSelector(selectPhoneNumbers);
  const detailsLoading = useSelector(selectDetailsLoading);
  const pipedriveDetailsContext = isPipedriveDetailsContext();
  const contextSearchApplied = useRef(false);
  const contextRecipientOpened = useRef(false);

  // Load labels; recipients load normally or after deal/person phone is resolved
  useEffect(() => {
    if (system_user_id) {
      dispatch(fetchLabels());
      if (!pipedriveDetailsContext) {
        dispatch(getRecipients({ page: 1 }));
      }
    }
  }, [system_user_id, pipedriveDetailsContext, dispatch]);

  // Deal/person details (first time): search by phone, open chat or new chat
  useEffect(() => {
    if (
      !system_user_id ||
      !pipedriveDetailsContext ||
      contextSearchApplied.current
    ) {
      return;
    }

    const query = getPrimaryPhoneSearchQuery(phoneNumbers);
    if (!query) {
      if (!detailsLoading) {
        contextSearchApplied.current = true;
        dispatch(getRecipients({ page: 1 }));
      }
      return;
    }

    contextSearchApplied.current = true;
    setSearchTerm(query);

    dispatch(getRecipients({ page: 1, searchKeyword: query }))
      .unwrap()
      .then((payload) => {
        if (contextRecipientOpened.current) return;
        contextRecipientOpened.current = true;

        const items = payload.items ?? [];
        if (items.length > 0) {
          const match =
            items.find(
              (item: Item) =>
                recipientMatchesSearchQuery(query, item.recipient.phone_number) ||
                (item.recipient.phone_number_string &&
                  recipientMatchesSearchQuery(
                    query,
                    item.recipient.phone_number_string
                  ))
            ) ?? items[0];

          dispatch(setCurrentRecipient(match.recipient));
          if (
            match.conversation.recipient !== "" &&
            match.conversation.status === "unread"
          ) {
            dispatch(
              updateMessageStatus({
                phone_number: match.conversation.phone_number,
                message_id: match.conversation.message_id,
                status: "read",
              })
            );
          }
        } else {
          setIsChatSendNewMessageOpen(true);
        }
      })
      .catch(() => {
        contextRecipientOpened.current = true;
      });
  }, [
    system_user_id,
    pipedriveDetailsContext,
    phoneNumbers,
    detailsLoading,
    dispatch,
  ]);

  const { recipients, loading, recipientsLoading, recipientsNextPage } =
    useSelector((state: RootState) => state.messenger);
  const isRecipientsLoading = recipientsLoading ?? loading;
  const { messages } = useSelector((state: RootState) => state.messenger);

  //socket options
  const options = {
    rememberUpgrade: true,
    transports: ["websocket"],
    secure: true,
    rejectUnauthorized: false,
  };

  const currentRecipientRef = useRef(currentRecipient);
  const currentLabelRef = useRef(labels);

  useEffect(() => {
    currentRecipientRef.current = currentRecipient; // Update ref when currentRecipient changes
  }, [currentRecipient]);

  useEffect(() => {
    currentLabelRef.current = labels;
  }, [labels]);

  useEffect(() => {
    // Only connect if we have a system_user_id
    if (!system_user_id) return;



    socket.current = io("https://chat-notifyer-backend.onrender.com/", {
      ...options,
      auth: {
        user_id: system_user_id
      }
    });

    const handleNewMessage = (message: Message) => {

      const currentRecipient = currentRecipientRef.current;
      const currentLabels = currentLabelRef.current;
      dispatch(
        newMessageByWebhook({
          message,
          currentRecipient,
          currentLabels,
          system_user_id,
        })
      );
    };

    const handleNewRecipientWithMessage = (newRecipient: Item) => {
      const currentRecipient = currentRecipientRef.current;
      dispatch(newRecipientWithMessage({ newRecipient, currentRecipient }));
    };

    const handleConnect = () => {
    };

    const handleDisconnect = () => {
    };

    const handleConnectError = () => {
    };

    // Set up event listeners
    socket.current.on("connect", handleConnect);
    socket.current.on("disconnect", handleDisconnect);
    socket.current.on("connect_error", handleConnectError);
    socket.current.on("newMessage", handleNewMessage);
    socket.current.on("newRecipientWithMessage", handleNewRecipientWithMessage);

    // Join user room for real-time updates
    socket.current.emit("joinUserRoom", { user_id: system_user_id });

    // Cleanup function
    return () => {
      if (socket.current) {
        socket.current.off("connect", handleConnect);
        socket.current.off("disconnect", handleDisconnect);
        socket.current.off("connect_error", handleConnectError);
        socket.current.off("newMessage", handleNewMessage);
        socket.current.off("newRecipientWithMessage", handleNewRecipientWithMessage);
        socket.current.disconnect();
        socket.current = null;
      }
    };
  }, [system_user_id, dispatch]);

  useEffect(() => {
    if (currentRecipient?.phone_number && system_user_id) {
      dispatch(
        getConversation({
          perPage: 500,
          pageNumber: 1,
          offset: 0,
          phoneNumber: currentRecipient?.phone_number,
        })
      );
    }
  }, [currentRecipient?.phone_number, dispatch, system_user_id]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.items]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);

    // If search is cleared, fetch normal data immediately
    if (newSearchTerm === "") {
      dispatch(resetRecipients());
      dispatch(
        getRecipients({
          page: 1,
          selectedLabels:
            selectedLabels.length > 0 ? selectedLabels : undefined,
        })
      );
    }
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchTerm.trim() !== "") {
      dispatch(
        getRecipients({
          page: 1,
          searchKeyword: searchTerm,
          selectedLabels:
            selectedLabels.length > 0 ? selectedLabels : undefined,
        })
      );
    }
  };

  //Load more recipients observer
  const observer = useRef<IntersectionObserver | null>(null);
  const lastRecipientRef = useCallback(
    (node: HTMLDivElement) => {
      if (isRecipientsLoading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && recipientsNextPage !== null) {
          dispatch(
            getRecipients({
              page: recipientsNextPage,
              searchKeyword: searchTerm,
              selectedLabels:
                selectedLabels.length > 0 ? selectedLabels : undefined,
            })
          );
        }
      });

      if (node) observer.current.observe(node);
    },
    [isRecipientsLoading, recipientsNextPage, dispatch, searchTerm, selectedLabels]
  );

  //handle AI -> Human Agent only
  const handleAiToHuman = () => {
    if (isSwitchOn) {
      if (currentRecipient && currentRecipient.id) {
        const { id, ...rest } = currentRecipient;
        setIsSwitchOn(false);
        dispatch(updateRecipient({ id, ...rest, is_ai_assistant: false }));
        setIsSwitchToHumanAgentPopupOpen(true);
        setTimeout(() => {
          setIsSwitchToHumanAgentPopupOpen(false);
        }, 3000);
      }
    }
  };

  //handle human agent/ai switch
  const handleSwitchChange = () => {
    if (isSwitchOn) {
      // If switching from true → false, show SwitchToHumanAgentPopup for 3 sec
      if (currentRecipient && currentRecipient.id) {
        const { id, ...rest } = currentRecipient;
        setIsSwitchOn(false);
        dispatch(updateRecipient({ id, ...rest, is_ai_assistant: false }));
        setIsSwitchToHumanAgentPopupOpen(true);
        setTimeout(() => {
          setIsSwitchToHumanAgentPopupOpen(false);
        }, 3000);
      }
    } else {
      // If switching from false → true, open AiAssistantPopup
      setIsSwitchToAiAssistantPopupOpen(true);
    }
  };

  const confirmSwitchToAiAssistant = () => {
    if (currentRecipient && currentRecipient.id) {
      const { id, ...rest } = currentRecipient;
      setIsSwitchToAiAssistantPopupOpen(false);
      setIsSwitchOn(true);
      dispatch(updateRecipient({ id, ...rest, is_ai_assistant: true }));
    }
  };

  const cancelSwitchToAiAssistant = () => {
    setIsSwitchToAiAssistantPopupOpen(false);
    setIsSwitchOn(false);
  };

  useEffect(() => {
    if (currentRecipient) {
      setIsSwitchOn(currentRecipient.is_ai_assistant);
    }
  }, [currentRecipient, currentRecipient?.is_ai_assistant]);

  //handle recipient last message status change
  const handleUpdateMessageStatus = (message: Message) => {
    if (message.recipient !== "" && message.status === "unread") {
      dispatch(
        updateMessageStatus({
          phone_number: message.phone_number,
          message_id: message.message_id,
          status: "read",
        })
      );
    }
  };

  //handle label slider
  const swiperRef = useRef<SwiperType>();
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const updateArrows = () => {
    if (swiperRef.current) {
      const swiper = swiperRef.current;
      const isStart = swiper.isBeginning;
      const isEnd = swiper.isEnd;

      setShowLeftArrow(!isStart);
      setShowRightArrow(!isEnd);
    }
  };

  // Update arrows when labels change
  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.update();
      requestAnimationFrame(() => {
        updateArrows();
      });
    }
  }, [selectedLabels]);

  // Handle label selection from dropdown
  const handleLabelSelect = (label: string) => {
    dispatch(addSelectedLabel(label));
    dispatch(
      getRecipients({
        page: 1,
        searchKeyword: searchTerm,
        selectedLabels: [...selectedLabels, label],
      })
    );
  };

  // Handle label removal from slider
  const handleLabelRemove = (label: string) => {
    dispatch(removeSelectedLabel(label));
    dispatch(removeSelectedLabel(label));
    const updatedLabels = selectedLabels.filter((l) => l !== label);

    // When removing last label, fetch normal data
    if (updatedLabels.length === 0) {
      dispatch(resetRecipients());
      dispatch(
        getRecipients({
          page: 1,
          searchKeyword: searchTerm,
        })
      );
    } else {
      // Otherwise fetch with remaining labels
      dispatch(
        getRecipients({
          page: 1,
          searchKeyword: searchTerm,
          selectedLabels: updatedLabels,
        })
      );
    }
  };


  return (
    <div className="flex h-full w-full flex-col items-start bg-default-background">
      <div className="flex w-full grow shrink-0 basis-0 items-start">
        <div className="flex max-w-[384px] grow shrink-0 basis-0 flex-col items-start self-stretch border-r border-solid border-neutral-border">
          {/* Left top */}
          <div className="flex w-full flex-col items-center gap-2 px-4 pt-4 pb-2 bg-gradient-to-b from-white to-gray-50/30 border-b border-gray-100 shadow-sm">
            <div className="flex w-full flex-col items-center gap-1 px-2 py-1">
              <div className="flex w-full items-center gap-2">
                <TextField
                  className="h-auto grow shrink-0 basis-0"
                  variant="filled"
                  label=""
                  helpText=""
                  icon="FeatherSearch"
                >
                  <TextField.Input
                    placeholder="Search conversations..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchSubmit}
                    className="rounded-lg"
                  />
                </TextField>
                <SubframeCore.DropdownMenu.Root>
                  <SubframeCore.DropdownMenu.Trigger asChild={true}>
                    <Button
                      variant="neutral-secondary"
                      icon="FeatherFilter"
                      className="hover:bg-gray-100 transition-all duration-200 rounded-lg"
                    // onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
                    >
                      Filter
                    </Button>
                  </SubframeCore.DropdownMenu.Trigger>
                  <SubframeCore.DropdownMenu.Portal>
                    <SubframeCore.DropdownMenu.Content
                      side="bottom"
                      align="start"
                      sideOffset={4}
                      asChild={true}
                    >
                      <DropdownMenu>
                        {labels.length > 0 ? (
                          labels.map((labelItem, index) => (
                            <DropdownMenu.DropdownItem
                              key={index}
                              icon={null}
                              className={`${selectedLabels.includes(labelItem.label)
                                ? "pointer-events-none bg-gray-100"
                                : ""
                                }`}
                              onClick={() => handleLabelSelect(labelItem.label)}
                            >
                              <div className="flex items-center justify-between w-full">
                                <span>{labelItem.label}</span>
                                {selectedLabels.includes(labelItem.label) && (
                                  <span className="text-gray-500 text-sm">
                                    Selected
                                  </span>
                                )}
                              </div>
                            </DropdownMenu.DropdownItem>
                          ))
                        ) : (
                          <DropdownMenu.DropdownItem
                            icon={null}
                            disabled={true}
                            className="text-neutral-500"
                          >
                            No labels available
                          </DropdownMenu.DropdownItem>
                        )}
                      </DropdownMenu>
                    </SubframeCore.DropdownMenu.Content>
                  </SubframeCore.DropdownMenu.Portal>
                </SubframeCore.DropdownMenu.Root>
              </div>
              {selectedLabels?.length > 0 && (
                <div className="flex w-full items-center justify-center gap-1 pt-1.5 z-0">
                  <IconButton
                    className={`h-6 w-5 flex-none ${showLeftArrow
                      ? "opacity-100"
                      : "opacity-0 pointer-events-none"
                      } transition-all duration-200`}
                    size="small"
                    icon="FeatherChevronLeft"
                    onClick={() => swiperRef.current?.slidePrev()}
                  />

                  <Swiper
                    modules={[Navigation]}
                    onBeforeInit={(swiper) => {
                      swiperRef.current = swiper;
                    }}
                    onSlideChange={updateArrows}
                    onAfterInit={updateArrows}
                    onResize={updateArrows}
                    slidesPerView="auto"
                    spaceBetween={8}
                    className="max-w-[288px] grow shrink-0 basis-0"
                    observer={true}
                    observeParents={true}
                  >
                    {selectedLabels.map((label, index) => (
                      <SwiperSlide key={index} className="!w-auto">
                        <div className="flex items-center gap-0.5 rounded-full border border-solid border-blue-200 bg-blue-50 pl-1 pr-1 py-0.5 shadow-sm hover:shadow-md transition-all duration-200">
                          <span className="whitespace-nowrap text-caption font-caption text-blue-700 font-medium">
                            {label}
                          </span>
                          <IconButton
                            size="small"
                            icon="FeatherX"
                            onClick={() => handleLabelRemove(label)}
                            className="hover:bg-blue-100 rounded-full transition-colors duration-200"
                          />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>

                  <IconButton
                    className={`h-6 w-5 flex-none ${showRightArrow
                      ? "opacity-100"
                      : "opacity-0 pointer-events-none"
                      } transition-all duration-200`}
                    size="small"
                    icon="FeatherChevronRight"
                    onClick={() => swiperRef.current?.slideNext()}
                  />
                </div>
              )}
              <div className="flex w-full items-start pt-2">
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setFilter("all")}
                    className={`px-2 py-2 text-xs rounded-md transition-all duration-200 font-medium ${
                      filter === "all" 
                        ? "bg-blue-500 text-white" 
                        : "bg-gray-50 text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setFilter("unread")}
                    className={`px-2 py-2 text-xs rounded-md transition-all duration-200 font-medium ${
                      filter === "unread" 
                        ? "bg-blue-500 text-white" 
                        : "bg-gray-50 text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                    }`}
                  >
                    Unread
                  </button>
                </div>
                <div className="flex w-full items-center justify-end gap-2 px-2">
                <IconButton
                  variant="brand-tertiary"
                  size="large"
                  icon="FeatherMessageSquarePlus"
                  onClick={() => setIsChatSendNewMessageOpen(true)}
                  className="hover:shadow-md transition-all duration-200 -mt-0.5"
                />
                <IconButton
                  variant="brand-tertiary"
                  size="large"
                  icon="FeatherSettings"
                  onClick={() => setIsSettingOpen(!isSettingOpen)}
                  className="hover:shadow-md transition-all duration-200 -mt-0.5"
                />
              </div>

              </div>
              {/* <div className="flex w-full items-center justify-end gap-2 px-2">
                <IconButton
                  variant="brand-tertiary"
                  size="large"
                  icon="FeatherMessageSquarePlus"
                  onClick={() => setIsChatSendNewMessageOpen(true)}
                  className="hover:shadow-md transition-all duration-200"
                />
                <IconButton
                  variant="brand-tertiary"
                  size="large"
                  icon="FeatherSettings"
                  onClick={() => setIsSettingOpen(!isSettingOpen)}
                  className="hover:shadow-md transition-all duration-200"
                />
              </div> */}
            </div>
          </div>
          {/* Left Bottom */}
          <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-4 px-4 py-4 overflow-auto custom-scrollbar relative">
            {/* Chat List UI */}
            <ChatList>
              {recipients && recipients.items && recipients.items.length > 0 ? (
                [...recipients.items]
                  .filter((item) => {
                    if (filter === "unread") {
                      return (
                        item.conversation?.recipient !== "" &&
                        item.conversation?.status === "unread"
                      );
                    }
                    return true; // Show all if filter is "all"
                  })
                  .sort((a, b) => {
                    const timeA = a.recipient.last_message_time
                      ? new Date(a.recipient.last_message_time).getTime()
                      : 0;

                    const timeB = b.recipient.last_message_time
                      ? new Date(b.recipient.last_message_time).getTime()
                      : 0;

                    return timeB - timeA; // Sort in descending order (latest first)
                  })
                  .map((item: Item, index: number) => (
                    <div
                      key={`${item.recipient.id}-${index}`}
                      ref={
                        index === recipients.items.length - 1
                          ? lastRecipientRef
                          : null
                      }
                      className={`flex w-full items-center gap-4 overflow-hidden rounded-md px-3 py-3 ${currentRecipient?.id === item.recipient.id
                        ? "bg-neutral-100"
                        : "bg-default-background"
                        }`}
                      onClick={() => {
                        dispatch(setCurrentRecipient(item.recipient));
                        handleUpdateMessageStatus(item.conversation);
                      }}
                    >
                      <Friend
                        recipient={item.recipient}
                        lastMessage={item.conversation}
                      />
                    </div>
                  ))
              ) : isRecipientsLoading ? (
                <div className="flex items-center justify-center w-full">
                  <Loader size="medium" />
                </div>
              ) : (
                <div className="flex w-full items-center justify-center px-3 py-3">
                  <span className="text-body font-body text-neutral-700">
                    No chat available
                  </span>
                </div>
              )}
            </ChatList>
            
            {/* Settings Modal - Rendered at root level */}
            <Settings
              setIsSettingOpen={setIsSettingOpen}
              isSettingOpen={isSettingOpen}
              setIsLabelCreationOpen={setIsLabelCreationOpen}
              setIsLabelDeleteOpen={setIsLabelDeleteOpen}
              setLabelId={setLabelId}
            />
            
            {/* Old Settings Slide-in Container - Keep for backward compatibility */}
            <div
              className={`absolute top-0 left-0 flex max-w-[384px] h-full bg-white grow shrink-0 basis-0 flex-col items-start gap-4 py-4 overflow-auto custom-scrollbar
                     transition-transform duration-300 ease-in-out
                     ${isSettingOpen ? "translate-x-0" : "-translate-x-full"}`}
            >
              {/* This container is now empty since Settings is rendered as modal above */}
            </div>
            <LabelCreation
              open={isLabelCreationOpen}
              setIsLabelCreationOpen={setIsLabelCreationOpen}
            />
            <DeleteLabelPopup
              open={isLabelDeleteOpen}
              setIsLabelDeleteOpen={setIsLabelDeleteOpen}
              labelId={labelId}
            />
            <SwitchToAiAssistantPopup
              open={isSwitchToAiAssistantPopupOpen}
              setIsSwitchToAiAssistantPopupOpen={
                setIsSwitchToAiAssistantPopupOpen
              }
              onConfirm={confirmSwitchToAiAssistant}
              onCancel={cancelSwitchToAiAssistant}
            />
            <SwitchToHumanAgent
              open={isSwitchToHumanAgentPopupOpen}
              setIsSwitchToHumanAgentPopupOpen={
                setIsSwitchToHumanAgentPopupOpen
              }
            />
            <ChatSendNewMessage
              isNewChatOpen={isChatSendNewMessageOpen}
              setIsNewChatOpen={setIsChatSendNewMessageOpen}
            />
            {/* <SelectDateAndTimePopup
                open={isSelectDateAndTimePopupOpen}
                setSelectDateAndTimePopupOpen={setSelectDateAndTimePopupOpen}
              /> */}

            <div className="hidden w-full grow shrink-0 basis-0 flex-col items-center justify-end gap-4">
              <ActionCard
                className="h-auto w-full flex-none"
                icon="FeatherSettings2"
                title="Chat Setting"
                desc="Usability tests, surveys, card sort, and more"
              />
            </div>
          </div>

          {/* Floating Button - Outside scrollable container but inside Chats section */}
          <div className="absolute bottom-4 left-4 z-0">
            <IconButton
              variant="brand-primary"
              size="large"
              icon="FeatherMessageSquarePlus"
              onClick={() => setIsChatSendNewMessageOpen(true)}
              className="rounded-full shadow-lg hover:shadow-xl transition-all duration-300 ease-in-out hover:scale-110 active:scale-95 bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500"
            />
          </div>

        </div>
        {/* Middle part- chat room */}
        {currentRecipient && currentRecipient.phone_number ? (
          <ChatRoom
            currentRecipient={currentRecipient}
            messages={messages}
            scrollRef={scrollRef}
            // setSelectDateAndTimePopupOpen={setSelectDateAndTimePopupOpen}
            isSwitchOn={isSwitchOn}
            onToggle={handleSwitchChange}
            handleAiToHuman={handleAiToHuman}
          />
        ) : (
          <div className="flex grow shrink-0 basis-0 flex-col items-center justify-center self-stretch border-l border-solid border-neutral-border">
            <div className="flex flex-col items-center gap-4 max-w-xl mx-auto px-4">
              {/* Logo Section */}
              <div className="flex flex-col items-center gap-3">
                <div className="relative">
                  <img
                    className="w-12 h-12 object-contain"
                    src="https://res.cloudinary.com/subframe/image/upload/v1732870928/uploads/4086/khdrdhexuo94bjzfwcz4.svg"
                    alt="Chat Notifyer Logo"
                  />
                  <div className="absolute -inset-1.5 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full blur-lg"></div>
                </div>
                <h1 className="text-xl font-bold text-gray-800 text-center">
                  Notifyer By WhatsAble Chat
                </h1>
              </div>

              {/* Description Text */}
              <p className="text-sm text-gray-600 text-center leading-relaxed max-w-md">
                Chat seamlessly with recipients, manage message templates, and organize conversations with custom labels - plus much more
              </p>

              {/* Language/Video Section */}
              <div className="flex w-full flex-col items-center gap-2 mt-1">
                <div className="flex w-full items-center justify-center gap-2">
                  <button
                    onClick={() => setSelectedLanguage('en')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                      selectedLanguage === 'en' 
                        ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm'
                    }`}
                  >
                    English
                  </button>
                  <button
                    onClick={() => setSelectedLanguage('es')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                      selectedLanguage === 'es' 
                        ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm'
                    }`}
                  >
                    Español
                  </button>
                  <button
                    onClick={() => setSelectedLanguage('pt')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                      selectedLanguage === 'pt' 
                        ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm'
                    }`}
                  >
                    Portuguese
                  </button>
                  <button
                    onClick={() => setSelectedLanguage('fr')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                      selectedLanguage === 'fr' 
                        ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm'
                    }`}
                  >
                    French
                  </button>
                </div>
                <div className="flex items-center justify-center overflow-hidden w-full max-w-md aspect-video bg-black relative rounded-lg shadow-md">
                  <iframe
                    key={selectedLanguage}
                    className="absolute top-0 left-0 w-full h-full rounded-lg"
                    src={videoUrls[selectedLanguage]}
                    title="Language Demo Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
            {/* Download App Banner */}
            <div className="mt-6 flex flex-col items-center gap-4 max-w-2xl mx-auto">
              <span className="text-base font-semibold text-gray-700 text-center">Download our app to answer on the go.</span>
              <div className="flex gap-3 mt-1">
                <a
                  href="https://apps.apple.com/es/app/notifyer-by-whatsable/id6743722183?l=en-GB"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block transition-transform hover:scale-105"
                >
                  <img
                    src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                    alt="Download on the App Store"
                    className="h-10"
                  />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.chatmobile.chat_mobile"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block transition-transform hover:scale-105"
                >
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                    alt="Get it on Google Play"
                    className="h-10"
                  />
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Messenger;
